import { useQuery } from '@tanstack/react-query';

import { dashboardV2Client } from '../api/client';
import { dashboardLiveQueryOptions } from '../api/liveQuery';
import { dashboardV2QueryKeys } from '../api/queryKeys';
import { CalendarWeekData, DashboardFilterValues } from '../api/types';

// Browser timezone drives day-column grouping so the week the user sees
// matches their local calendar; the server falls back to America/New_York.
const browserTimeZone = () => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
    );
  } catch {
    return 'America/New_York';
  }
};

const useCalendarWeek = (
  agencyId: number,
  startDate: string,
  filters: DashboardFilterValues,
  // 7 for the week grid, 1 for the Day view (GRAVY-10329). Part of the query
  // key, so switching view rotates the cache rather than showing the other
  // view's payload for a frame.
  days = 7,
) => {
  const timeZone = browserTimeZone();

  return useQuery<CalendarWeekData>({
    queryKey: dashboardV2QueryKeys.calendarWeek(
      agencyId,
      startDate,
      timeZone,
      filters,
      days,
    ),
    queryFn: () =>
      dashboardV2Client.getCalendarWeek({
        agencyId,
        startDate,
        timeZone,
        filters,
        days,
      }),
    enabled: agencyId != null && !!startDate,
    ...dashboardLiveQueryOptions,
    // Keep the current week on screen while a refresh (or a week change) is in
    // flight, so polling never blanks the grid.
    placeholderData: (prev) => prev,
  });
};

export default useCalendarWeek;
